import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Image, Swiper, ImageViewer } from 'antd-mobile'
import { DownFill } from 'antd-mobile-icons'
import { getAlbums } from '../../model/jimu'
import { renderImageUrl, replaceCDNDomain, formatJSON } from '../../utils'
import { getLanguageStore } from '../../utils/storage/language'
import { getFromView } from '../../utils/session/store'
import { getMemberInfo } from '../../utils/storage/store'
import { getUrlHashName } from '../../utils/page'
import { addBehaviorsRecord } from '../../utils/BehaviorstatsHelp'

import { getActivityModel, getCouponRuleModel } from '../../model/store'

import KeepAlivePopup from '../KeepAlivePopup'
import PopupContent from '../PopupContent'
import PromotionDetail from '../PromotionList/Detail'
import CouponModal from '../PromotionList/CouponModal'
import BookingModal from '../PromotionList/BookingModal'

import styles from './index.module.less'

import defImg from '../../assets/images/no_img.jpg'

// 常量定义
const CONTENT_TYPE = {
    COUPON: 3,
    BOOKING: [1, 4, 5, 6]
}

const AUTO_POPUP_PARAM = '1'
const COUPON_POPUP_DELAY = 1000
const ACTIVITY_FETCH_DELAY = 1100

export default function TopAlbum({ albumId, storeInfo, from = 'merchant', getActFromRes = true }) {

    const [state, setState] = useState({
        album: null,
        pictures: [],
        currentCategory: null,
        actDetail: null
    })

    const [popupState, setPopupState] = useState({
        couponVisible: false,
        curCoupon: null,
        bookingBoxVisible: false,
        bookingProducts: []
    })

    const swiperRef = useRef(null);
    const navigate = useNavigate()
    const { memberId = '' } = getMemberInfo() || {}
    const [isFullscreen, setIsFullscreen] = useState(false)

    // 处理图片点击
    const handleImageClick = useCallback((img) => {
        if (!state.pictures || state.pictures.length === 0) {
            console.warn('No pictures available for image viewer');
            return;
        }
        if (img.mediaType === 'video') {
            return
        }
        const index = state.pictures.findIndex(item => item.imageId === img.imageId)
        const imageUrls = state.pictures
            .filter(item => item.mediaType !== 'video')
            .map(item => replaceCDNDomain(item.filePath))
        ImageViewer.Multi.show({ images: imageUrls, defaultIndex: index })
        addBehaviorsRecord({
            behaviorType: 160200,
            behavior: 160204,
            storeId: storeInfo.storeId,
            value1: `${from}-album-picture`,
            value2: getUrlHashName()
        })
    }, [state.pictures]);

    // 处理视频点击 - 直接使用原生全屏播放（竖屏）
    const handleVideoClick = useCallback((e) => {
        const videoElement = e.currentTarget.querySelector('video');
        if (!videoElement) return;

        // 尝试全屏播放
        const enterFullscreen = async () => {
            try {
                // 先尝试播放视频（如果暂停的话）
                if (videoElement.paused) {
                    await videoElement.play();
                }

                // 锁定屏幕方向为竖屏
                const lockOrientation = async () => {
                    try {
                        if (window.screen.orientation && window.screen.orientation.lock) {
                            await window.screen.orientation.lock('portrait').catch(err => {
                                console.log('屏幕方向锁定失败（可能不支持）:', err);
                            });
                        }
                    } catch (err) {
                        console.log('屏幕方向 API 不可用:', err);
                    }
                };

                // iOS Safari 特殊处理
                if (videoElement.webkitEnterFullscreen) {
                    await lockOrientation();
                    videoElement.webkitEnterFullscreen();
                }
                // 标准全屏 API
                else if (videoElement.requestFullscreen) {
                    await videoElement.requestFullscreen();
                    await lockOrientation();
                }
                // Webkit 浏览器
                else if (videoElement.webkitRequestFullscreen) {
                    await videoElement.webkitRequestFullscreen();
                    await lockOrientation();
                }
                // Firefox
                else if (videoElement.mozRequestFullScreen) {
                    await videoElement.mozRequestFullScreen();
                    await lockOrientation();
                }
                // IE/Edge
                else if (videoElement.msRequestFullscreen) {
                    await videoElement.msRequestFullscreen();
                    await lockOrientation();
                }

                // 监听退出全屏事件，解锁屏幕方向
                const unlockOrientation = () => {
                    try {
                        if (window.screen.orientation && window.screen.orientation.unlock) {
                            window.screen.orientation.unlock();
                        }
                    } catch (err) {
                        console.log('解锁屏幕方向失败:', err);
                    }
                };

                videoElement.addEventListener('webkitendfullscreen', () => {
                    unlockOrientation();
                    setIsFullscreen(false);
                }, { once: true });
                document.addEventListener('fullscreenchange', () => {
                    if (!document.fullscreenElement) {
                        unlockOrientation();
                        setIsFullscreen(false);
                    } else {
                        setIsFullscreen(true);
                    }
                }, { once: true });

            } catch (err) {
                console.error('全屏播放失败:', err);
            }
        };

        enterFullscreen();

        addBehaviorsRecord({
            behaviorType: 160200,
            behavior: 160204,
            storeId: storeInfo.storeId,
            value1: `${from}-album-video`,
            value2: getUrlHashName()
        })
    }, []);

    // 处理分类点击
    const handleCategoryClick = useCallback((item) => {
        if (item.id === state.currentCategory?.id) {
            return
        }
        swiperRef.current?.swipeTo(0)
        setState(prevState => ({
            ...prevState,
            currentCategory: item,
            pictures: item.images
        }));
    }, [state.currentCategory?.id])

    // 处理查看更多
    const handleCategoryMoreClick = useCallback(() => {
        navigate(`/albums?id=${albumId}`, {
            state: { storeInfo }
        })
    }, [albumId, navigate, storeInfo])

    // 获取优惠券规则
    const getCouponRule = useCallback(async (ids) => {
        try {
            const res = await getCouponRuleModel({
                couponRuleIds: ids,
                curLanguage: getLanguageStore,
                memberId
            }, true)
            return res.filter(item => !item.couponState)
        } catch (error) {
            console.error('获取优惠券规则失败:', error)
            return []
        }
    }, [memberId])

    // 显示优惠券弹窗
    const popupCouponContent = useCallback(async (item) => {
        let nObj = {}
        if (Array.isArray(item)) {
            if (item.length === 0) return

            if (item.length === 1) {
                nObj = { ...item[0] }
            } else {
                nObj.list = item
                nObj.param2 = item.map(o => o.param2).join(',')
            }
        }
        if (!nObj?.param2) return

        const couponList = await getCouponRule(nObj.param2)
        if (couponList.length > 0) {
            setTimeout(() => {
                setPopupState(prev => ({ ...prev, couponVisible: true, curCoupon: nObj }))
            }, COUPON_POPUP_DELAY)
        }
    }, [getCouponRule])

    // 显示预订弹窗
    const popupBookingBox = useCallback((arr) => {
        if (arr.length > 0) {
            setPopupState(prev => ({ ...prev, bookingBoxVisible: true, bookingProducts: arr }))
        }
    }, [])

    // 获取活动列表
    const getActivityList = useCallback(() => {
        if (!storeInfo?.storeId) return

        getActivityModel({
            relateId: storeInfo.storeId,
            positions: '401,403,407',
            relateType: 801,
            curLanguage: getLanguageStore(),
        }).then(res => {
            const fromView = getFromView()
            if (from === 'merchant' && !fromView) {
                let nList = [...res]
                const { storeArea } = storeInfo
                const { roomTypeId } = storeArea || {}
                if (roomTypeId) {
                    nList = nList.filter(item => {
                        const { extendColumn } = item
                        const extendObj = formatJSON(extendColumn)
                        if (extendObj && extendObj.notApplicableRoomTypeIds) {
                            const notApplicableRoomTypeIds = extendObj.notApplicableRoomTypeIds.split(',').map(id => parseInt(id))
                            return !notApplicableRoomTypeIds.includes(roomTypeId)
                        }
                        return true
                    })
                }
                const couponItem = nList.filter(item =>
                    item.contentType === CONTENT_TYPE.COUPON && item.param3 === AUTO_POPUP_PARAM
                )
                const bookingProducts = nList.filter(item =>
                    CONTENT_TYPE.BOOKING.includes(item.contentType) && item.param3 === AUTO_POPUP_PARAM
                )
                popupCouponContent(couponItem)
                popupBookingBox(bookingProducts)
            }
        }).catch(err => {
            console.error('获取活动列表失败:', err)
        })
    }, [storeInfo, from, popupCouponContent, popupBookingBox])

    // 获取活动数据
    useEffect(() => {
        if (storeInfo?.storeId && from === 'merchant' && getActFromRes) {
            const timer = setTimeout(() => {
                getActivityList()
            }, ACTIVITY_FETCH_DELAY)
            return () => clearTimeout(timer)
        }
    }, [getActFromRes, storeInfo?.storeId, from, getActivityList])

    // 获取相册数据
    useEffect(() => {
        if (!albumId) return;

        getAlbums(albumId).then(res => {
            const firstCategory = res.categories?.[0];

            if (!firstCategory?.images) {
                console.warn('No images found in album');
                setState(prevState => ({
                    ...prevState,
                    album: res,
                    pictures: []
                }));
                return;
            }

            setState(prevState => ({
                ...prevState,
                album: res,
                pictures: firstCategory.images,
                currentCategory: firstCategory
            }));
        }).catch(err => {
            console.error('获取相册失败:', err)
            setState(prevState => ({
                ...prevState,
                pictures: []
            }));
        })
    }, [albumId])


    const { album, pictures } = state

    // 检测是否有视频
    const hasVideo = useMemo(() =>
        pictures.some(item => item.mediaType === 'video'),
        [pictures]
    )

    // 排序媒体列表：视频优先，图片保持原始顺序
    const sortedMedia = useMemo(() => {
        return [...pictures].sort((a, b) => {
            // 如果 a 是 video，b 不是，a 排在前面
            if (a.mediaType === 'video' && b.mediaType !== 'video') {
                return -1;
            }
            // 如果 b 是 video，a 不是，b 排在前面
            if (b.mediaType === 'video' && a.mediaType !== 'video') {
                return 1;
            }
            // 都是 video 或都不是 video，保持原始顺序
            return 0;
        })
    }, [pictures])

    if (!album) return null
    return (
        <div className={styles.topAlbumWrapper}>
            {sortedMedia.length > 0 && (
                <Swiper
                    ref={swiperRef}
                    autoplay={!hasVideo}
                    loop
                    autoplayInterval={3000}
                    indicator={() => null}
                >
                    {
                        sortedMedia.map((img) => (
                            <Swiper.Item key={img.imageId}>
                                {img.mediaType === 'video' ?
                                    (
                                        <div
                                            className={styles.albumItem}
                                            style={{
                                                position: 'relative',
                                                aspectRatio: 1.6,
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                backgroundColor: '#000'
                                            }}
                                            onClick={handleVideoClick}
                                        >
                                            {/* 自动播放的视频 */}
                                            <video
                                                src={replaceCDNDomain(img.filePath)}
                                                autoPlay
                                                muted
                                                loop
                                                playsInline
                                                controls={false}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: isFullscreen ? 'contain' : 'cover'
                                                }}
                                                onLoadedMetadata={(e) => {
                                                    const video = e.target;

                                                    // 设置移动端内联播放属性
                                                    video.setAttribute('webkit-playsinline', 'true');
                                                    video.setAttribute('x5-playsinline', 'true');
                                                    video.setAttribute('x5-video-player-type', 'h5');

                                                    // iOS Safari 全屏事件
                                                    video.addEventListener('webkitbeginfullscreen', () => {
                                                        video.controls = true;
                                                        video.muted = false;
                                                        setIsFullscreen(true);
                                                    });
                                                    video.addEventListener('webkitendfullscreen', () => {
                                                        video.controls = false;
                                                        video.muted = true;
                                                        setIsFullscreen(false);
                                                    });

                                                    // 标准全屏 API 事件
                                                    const handleFullscreenChange = () => {
                                                        if (document.fullscreenElement === video) {
                                                            video.controls = true;
                                                            video.muted = false;
                                                            setIsFullscreen(true);
                                                        } else if (document.fullscreenElement === null) {
                                                            video.controls = false;
                                                            video.muted = true;
                                                            setIsFullscreen(false);
                                                        }
                                                    };

                                                    document.addEventListener('fullscreenchange', handleFullscreenChange);
                                                }}
                                            />
                                            {/* 轻微的点击提示遮罩 */}
                                            <div style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: 50,
                                                height: 50,
                                                borderRadius: '50%',
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                pointerEvents: 'none',
                                                opacity: 0.8
                                            }}>
                                                <div style={{
                                                    width: 0,
                                                    height: 0,
                                                    borderLeft: '16px solid #000',
                                                    borderTop: '10px solid transparent',
                                                    borderBottom: '10px solid transparent',
                                                    marginLeft: 3
                                                }} />
                                            </div>
                                        </div>
                                    )
                                    :
                                    (
                                        <div
                                            className={`${styles.albumItem} `}
                                            onClick={() => handleImageClick(img)}>
                                            <Image
                                                src={renderImageUrl({ size: 'b', src: img.filePath })}
                                                alt=""
                                                lazy
                                                fit='cover'
                                                placeholder={<Image src={defImg} />}
                                                onError={e => {
                                                    e.target.onerror = null;
                                                    e.target.src = defImg;
                                                }} />
                                        </div>
                                    )}

                            </Swiper.Item>

                        ))
                    }
                </Swiper>
            )}
            <ul className={styles.albumCategory}>
                {
                    album.categories.slice(0, 3).map((item) => (
                        <li
                            className={`${styles.albumCategoryItem} ${item.id === state.currentCategory?.id ? styles.active : ''}`}
                            key={item.id}
                            onClick={() => handleCategoryClick(item)}>
                            {item.categoryName}
                        </li>
                    ))
                }
                <li className={`${styles.albumCategoryMore} jm-flex`} onClick={handleCategoryMoreClick}>
                    <DownFill color='#fff' style={{ transform: 'rotate(-90deg)' }} />
                </li>
            </ul>

            {/* 详情弹窗 */}
            {state.actDetail && (
                <KeepAlivePopup
                    visible={!!state.actDetail}
                    onClose={() => setState(prev => ({ ...prev, actDetail: null }))}
                    position='bottom'
                >
                    <PopupContent>
                        <PromotionDetail
                            detail={{ ...state.actDetail, placeFlag: 1 }}
                            callback={() => setState(prev => ({ ...prev, actDetail: null }))}
                        />
                    </PopupContent>
                </KeepAlivePopup>
            )}

            {/* 优惠券弹窗 */}
            <CouponModal
                visible={popupState.couponVisible}
                coupon={popupState.curCoupon}
                onClose={() => setPopupState(prev => ({ ...prev, couponVisible: false }))}
                storeId={storeInfo.storeId}
                placeFlag={1}
            />

            {/* 预订弹窗 */}
            <BookingModal
                visible={popupState.bookingBoxVisible}
                products={popupState.bookingProducts}
                onClose={() => setPopupState(prev => ({ ...prev, bookingBoxVisible: false }))}
                onItemClick={(item) => {
                     //预订弹窗点击行为统计
                     addBehaviorsRecord({
                        behaviorType: 160100,
                        behavior: 160101,
                        storeId: storeInfo.storeId,
                        value1: 1,
                        value2: item.activityId,
                        value3: item.contentType
                    })
                    setState(prev => ({ ...prev, actDetail: item }))
                }}
            />
        </div>
    )
}
